import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, Trash2, X, Bell, Check,
  Clock, Repeat, ChevronDown, Edit3, ArrowLeft, Briefcase,
} from 'lucide-react';
import { toast } from 'sonner';
import CalendarOverview from './CalendarOverview';
import CalendarSync from './CalendarSync';
import DailyPlanner from './DailyPlanner';
import WorkShiftQuickAdd from './WorkShiftQuickAdd';
import SpeakButton from './SpeakButton';
import DictateButton from './DictateButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS_SV = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];
const WEEKDAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAYS_SV_FULL = ['Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag', 'Söndag'];

const RECURRENCE_LABELS = {
  none: 'Engångs',
  daily: 'Dagligen',
  weekly: 'Varje vecka',
  monthly: 'Varje månad',
  yearly: 'Varje år',
};

const REMINDER_LABELS = {
  0: 'Vid start',
  5: '5 min innan',
  15: '15 min innan',
  30: '30 min innan',
  60: '1 tim innan',
  1440: '1 dag innan',
};

// Pastel column backgrounds (pink / lavender / sky blue rotation)
const COLUMN_PASTELS = [
  '#FFE8F0', // pink
  '#FFEDF5', // lighter pink
  '#F3E8FF', // lavender
  '#EDE4FF', // purple
  '#E8F0FF', // sky
  '#E0EBFF', // blue
  '#F5EFFF', // soft purple
];

function fmtDateLocal(d) {
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function fmtTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
}

function isoInLocalTz(dateStr, timeStr) {
  return `${dateStr}T${timeStr || '00:00'}:00`;
}

function EventEditor({ existing, categories, recurrenceOptions, reminderOptions, defaultDate, onSave, onCancel, onDelete }) {
  const initStart = existing?.starts_at ? new Date(existing.starts_at) : (defaultDate || new Date());
  const initEnd = existing?.ends_at ? new Date(existing.ends_at) : new Date(initStart.getTime() + 60 * 60 * 1000);
  const [title, setTitle] = useState(existing?.title || '');
  const [description, setDescription] = useState(existing?.description || '');
  const [date, setDate] = useState(fmtDateLocal(initStart));
  const [startTime, setStartTime] = useState(initStart.toTimeString().slice(0, 5));
  const [endTime, setEndTime] = useState(initEnd.toTimeString().slice(0, 5));
  const [allDay, setAllDay] = useState(existing?.all_day || false);
  const [category, setCategory] = useState(existing?.category || 'personal');
  const [recurrence, setRecurrence] = useState(existing?.recurrence || 'none');
  const [reminderMinutes, setReminderMinutes] = useState(existing?.reminder_minutes ?? null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) { toast.error('Skriv en titel'); return; }
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        description: description.trim(),
        starts_at: allDay ? `${date}T00:00:00` : isoInLocalTz(date, startTime),
        ends_at: allDay ? `${date}T23:59:00` : isoInLocalTz(date, endTime),
        all_day: allDay,
        category, recurrence, reminder_minutes: reminderMinutes,
      });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="calendar-editor">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative neo-card p-5 bg-white dark:bg-[#252545] w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-bold font-['Fredoka'] text-lg">{existing ? 'Redigera händelse' : 'Ny händelse'}</h4>
          <button onClick={onCancel} className="neo-btn p-1 bg-white"><X className="w-4 h-4" /></button>
        </div>

        <input
          type="text"
          placeholder="Titel (t.ex. Läkarbesök)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={160}
          autoFocus
          className="w-full px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]"
          data-testid="calendar-title"
        />

        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          maxLength={1000}
          placeholder="Beskrivning (valfritt)"
          className="w-full px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54] resize-none"
          data-testid="calendar-description"
        />

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} data-testid="calendar-allday" />
          <span>Heldag</span>
        </label>

        <div className="grid grid-cols-3 gap-2">
          <label className="block col-span-3 md:col-span-1">
            <span className="text-xs font-bold block mb-1">Datum</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="w-full px-2 py-1.5 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]" />
          </label>
          {!allDay && (
            <>
              <label className="block">
                <span className="text-xs font-bold block mb-1">Start</span>
                <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                  className="w-full px-2 py-1.5 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]" />
              </label>
              <label className="block">
                <span className="text-xs font-bold block mb-1">Slut</span>
                <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
                  className="w-full px-2 py-1.5 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]" />
              </label>
            </>
          )}
        </div>

        <div>
          <p className="text-xs font-bold mb-1">Kategori</p>
          <div className="flex flex-wrap gap-1">
            {Object.entries(categories).map(([k, c]) => (
              <button key={k} onClick={() => setCategory(k)} type="button"
                className={`neo-btn px-2 py-1 text-xs flex items-center gap-1 ${category === k ? 'ring-2 ring-black' : ''}`}
                style={category === k ? { backgroundColor: c.color } : {}}>
                {c.emoji} {c.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-bold mb-1 flex items-center gap-1"><Repeat className="w-3 h-3" /> Återkommande</p>
          <div className="flex flex-wrap gap-1">
            {recurrenceOptions.map((r) => (
              <button key={r} onClick={() => setRecurrence(r)} type="button"
                className={`neo-btn px-2 py-1 text-xs ${recurrence === r ? 'bg-[#C8B6FF] ring-2 ring-black' : 'bg-white'}`}>
                {RECURRENCE_LABELS[r] || r}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-bold mb-1 flex items-center gap-1"><Bell className="w-3 h-3" /> Påminnelse</p>
          <div className="flex flex-wrap gap-1">
            <button onClick={() => setReminderMinutes(null)} type="button"
              className={`neo-btn px-2 py-1 text-xs ${reminderMinutes === null ? 'bg-[#FFD6E8] ring-2 ring-black' : 'bg-white'}`}>
              Ingen
            </button>
            {reminderOptions.map((m) => (
              <button key={m} onClick={() => setReminderMinutes(m)} type="button"
                className={`neo-btn px-2 py-1 text-xs ${reminderMinutes === m ? 'bg-[#FFD6E8] ring-2 ring-black' : 'bg-white'}`}>
                {REMINDER_LABELS[m] || `${m} min`}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <button onClick={save} disabled={saving}
            className="neo-btn flex-1 py-2 bg-[#C8B6FF] text-sm font-bold disabled:opacity-50">
            {saving ? 'Sparar…' : existing ? 'Spara' : 'Lägg till'}
          </button>
          {existing && onDelete && (
            <button onClick={() => onDelete(existing)}
              className="neo-btn px-3 py-2 bg-white hover:bg-red-100 text-sm">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Builds the bullet-journal-style 7-column layout (Mon..Sun down each column)
function buildColumnDays(year, month) {
  // Start at first Monday (may be in previous month)
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // Mon=0
  const gridStart = new Date(year, month, 1 - startOffset);
  // 6 weeks = 42 days
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  // Organize by weekday column
  const byWeekday = [[], [], [], [], [], [], []];
  for (let i = 0; i < 42; i++) {
    const wd = (cells[i].getDay() + 6) % 7;
    byWeekday[wd].push(cells[i]);
  }
  return byWeekday; // 7 arrays, each with 6 dates
}

function DayCell({ date, events, categories, currentMonth, onClick, onEventClick }) {
  const key = fmtDateLocal(date);
  const dayEvents = events[key] || [];
  const isCurrentMonth = date.getMonth() === currentMonth;
  const isToday = key === fmtDateLocal(new Date());
  // Mini pet reaction based on how busy the day is
  const busyEmoji = dayEvents.length === 0
    ? null
    : dayEvents.length <= 3
      ? '🧘'
      : '🏃';
  return (
    <button
      onClick={() => onClick(date)}
      className={`group w-full text-left border-b border-black/30 hover:bg-white/50 transition-colors relative p-1 ${isCurrentMonth ? '' : 'opacity-40'}`}
      style={{ minHeight: '56px' }}
      data-testid={`bujo-day-${key}`}
    >
      <div className="flex items-baseline gap-1 border-b border-black/20 pb-0.5 mb-0.5">
        <span className={`text-[11px] font-bold ${isToday ? 'text-pink-600' : 'text-gray-500'}`}>
          {date.getDate()}
        </span>
        {isToday && (
          <span className="text-[8px] bg-[#FFD6E8] border border-black rounded-full px-1 font-bold leading-tight">idag</span>
        )}
        {busyEmoji && (
          <span
            className="ml-auto text-[10px] opacity-70 group-hover:opacity-0 transition-opacity leading-none"
            title={dayEvents.length <= 3 ? 'Lugn dag' : 'Fullt schema'}
            data-testid={`bujo-pet-mood-${key}`}
          >
            {busyEmoji}
          </span>
        )}
        <Plus className={`w-2.5 h-2.5 ${busyEmoji ? 'absolute right-1 top-1' : 'ml-auto'} opacity-0 group-hover:opacity-60 transition-opacity`} />
      </div>
      <div className="space-y-0.5">
        {dayEvents.slice(0, 2).map((ev, i) => {
          const c = categories[ev.category] || { color: '#ddd', emoji: '•' };
          return (
            <div
              key={i}
              onClick={(e) => { e.stopPropagation(); onEventClick(ev); }}
              className={`text-[9px] px-1 py-0 rounded border border-black truncate leading-tight flex items-center gap-1 ${ev.completed ? 'line-through opacity-50' : ''}`}
              style={{ backgroundColor: c.color }}
              title={ev.title}
            >
              {!ev.all_day && <span className="opacity-70 font-mono">{fmtTime(ev.starts_at).slice(0, 5)}</span>}
              <span className="truncate">{ev.title}</span>
            </div>
          );
        })}
        {dayEvents.length > 2 && (
          <div className="text-[8px] opacity-60 italic leading-tight">+{dayEvents.length - 2} till</div>
        )}
      </div>
    </button>
  );
}

function TasksSidebar({ tasks, onToggle }) {
  return (
    <div className="neo-card bg-[#FFE8F0] p-3 border-[3px]" data-testid="bujo-tasks">
      <h3 className="font-['Fredoka'] font-bold text-lg mb-2">Tasks</h3>
      <div className="space-y-1 max-h-[380px] overflow-y-auto pr-1">
        {tasks.length === 0 ? (
          <p className="text-xs opacity-60 italic">Inga tasks</p>
        ) : tasks.map((t) => (
          <label key={t.id} className="flex items-start gap-1.5 text-xs cursor-pointer hover:bg-white/50 rounded px-1 py-0.5" data-testid={`bujo-task-${t.id}`}>
            <input
              type="checkbox"
              checked={!!t.is_completed}
              onChange={() => onToggle(t)}
              className="mt-0.5 flex-shrink-0"
            />
            <span className={`${t.is_completed ? 'line-through opacity-50' : ''} break-words`}>{t.title}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function NotesSidebar({ value, onChange }) {
  return (
    <div className="neo-card bg-[#E8F0FF] p-3 border-[3px]" data-testid="bujo-notes">
      <h3 className="font-['Fredoka'] font-bold text-lg mb-2">Notes</h3>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Skriv dina månadens anteckningar här…"
        className="w-full min-h-[260px] bg-transparent outline-none text-sm resize-none leading-relaxed"
        data-testid="bujo-notes-input"
      />
    </div>
  );
}

function DayDetail({ date, events, categories, onClose, onAddEvent, onEditEvent, onDeleteEvent, onAddShift }) {
  const dateStr = fmtDateLocal(date);
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [dateStr]);
  const prettyDate = date.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <div ref={ref} className="neo-card p-5 bg-gradient-to-br from-[#FFE8F0] via-[#F3E8FF] to-[#E8F0FF] border-[3px]" data-testid="day-detail">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="day-detail-close">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <p className="text-[11px] opacity-60 uppercase tracking-wider">Dag</p>
            <h3 className="font-bold font-['Fredoka'] text-lg capitalize">{prettyDate}</h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onAddShift}
            className="neo-btn px-3 py-1.5 bg-[#B9F5D8] text-sm font-bold flex items-center gap-1"
            data-testid="day-detail-add-shift"
            title="Lägg till arbetspass"
          >
            <Briefcase className="w-4 h-4" /> Dagens jobb
          </button>
          <button
            onClick={onAddEvent}
            className="neo-btn px-3 py-1.5 bg-[#C8B6FF] text-sm font-bold flex items-center gap-1"
            data-testid="day-detail-add-event"
          >
            <Plus className="w-4 h-4" /> Ny händelse
          </button>
        </div>
      </div>

      {/* Events for this day */}
      <div className="neo-card bg-white p-3 mb-3" data-testid="day-detail-events">
        <h4 className="font-['Fredoka'] font-bold text-sm mb-2 flex items-center gap-1">
          <CalendarDays className="w-4 h-4" /> Händelser ({events.length})
        </h4>
        {events.length === 0 ? (
          <p className="text-xs opacity-60 italic py-3 text-center">Inga händelser denna dag. Klicka "Ny händelse" för att lägga till.</p>
        ) : (
          <div className="space-y-1.5">
            {events.map((ev, i) => {
              const c = categories[ev.category] || { color: '#ddd', emoji: '•' };
              return (
                <div
                  key={i}
                  className={`rounded-xl border-2 border-black p-2 flex items-center gap-2 ${ev.completed ? 'opacity-60' : ''}`}
                  style={{ backgroundColor: c.color + '90' }}
                >
                  <span className="text-lg">{c.emoji}</span>
                  <button onClick={() => onEditEvent(ev)} className="flex-1 text-left min-w-0">
                    <p className={`font-bold text-sm truncate ${ev.completed ? 'line-through' : ''}`}>
                      {!ev.all_day && <span className="font-mono text-[11px] opacity-70 mr-1">{fmtTime(ev.starts_at)}</span>}
                      {ev.title}
                    </p>
                    {ev.description && <p className="text-[11px] opacity-70 truncate">{ev.description}</p>}
                  </button>
                  {ev.recurrence !== 'none' && <Repeat className="w-3 h-3 opacity-50" />}
                  {ev.reminder_minutes !== null && ev.reminder_minutes !== undefined && (
                    <Bell className="w-3 h-3 opacity-60" />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Daily Planner for this day */}
      <div className="neo-card bg-white p-2" data-testid="day-detail-planner">
        <DailyPlanner date={dateStr} />
      </div>
    </div>
  );
}


export default function CalendarPage({ onPointsChange }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [meta, setMeta] = useState({ categories: {}, recurrence_options: [], reminder_options: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [shiftDate, setShiftDate] = useState(null);
  const [defaultDate, setDefaultDate] = useState(null);
  const [notes, setNotes] = useState('');
  const [selectedDay, setSelectedDay] = useState(null); // Date | null
  const firedReminders = useRef(new Set());
  const notesSaveTimer = useRef(null);

  // Load persistent note per month
  useEffect(() => {
    const k = `bujo-notes-${cursor.year}-${cursor.month}`;
    setNotes(localStorage.getItem(k) || '');
  }, [cursor]);

  const saveNotes = (val) => {
    setNotes(val);
    clearTimeout(notesSaveTimer.current);
    notesSaveTimer.current = setTimeout(() => {
      localStorage.setItem(`bujo-notes-${cursor.year}-${cursor.month}`, val);
    }, 400);
  };

  const fetchEvents = useCallback(async () => {
    const fetchFrom = new Date(cursor.year, cursor.month - 1, 1);
    const fetchTo = new Date(cursor.year, cursor.month + 2, 1);
    try {
      const [ev, ts] = await Promise.all([
        axios.get(`${API}/calendar/events?from_date=${fetchFrom.toISOString()}&to_date=${fetchTo.toISOString()}`, { withCredentials: true }),
        axios.get(`${API}/tasks`, { withCredentials: true }),
      ]);
      setEvents(ev.data.items || []);
      setMeta({
        categories: ev.data.categories || {},
        recurrence_options: ev.data.recurrence_options || ['none'],
        reminder_options: ev.data.reminder_options || [],
      });
      // Tasks API response shape - try both common patterns
      const tasksList = Array.isArray(ts.data) ? ts.data : (ts.data.items || ts.data.tasks || []);
      setTasks(tasksList.slice(0, 30));
    } catch { /* noop */ }
  }, [cursor]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchEvents();
      setLoading(false);
    })();
  }, [fetchEvents]);

  // Browser reminders
  useEffect(() => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const tick = () => {
      const now = Date.now();
      events.forEach((ev) => {
        if (ev.reminder_minutes === null || ev.reminder_minutes === undefined) return;
        if (ev.completed) return;
        const triggerAt = new Date(ev.starts_at).getTime() - ev.reminder_minutes * 60 * 1000;
        const id = `${ev.event_id}-${ev.occurrence_date}`;
        if (firedReminders.current.has(id)) return;
        if (now >= triggerAt && now - triggerAt < 90 * 1000) {
          try {
            new Notification(`🔔 ${ev.title}`, {
              body: `${fmtTime(ev.starts_at)} · ${meta.categories[ev.category]?.label || ''}`,
              tag: id,
            });
            firedReminders.current.add(id);
          } catch { /* ignore */ }
        }
      });
    };
    tick();
    const interval = setInterval(tick, 30000);
    return () => clearInterval(interval);
  }, [events, meta.categories]);

  const requestNotif = async () => {
    if (!('Notification' in window)) { toast.error('Notiser stöds inte här'); return; }
    const p = await Notification.requestPermission();
    if (p === 'granted') toast.success('Påminnelser aktiverade 🔔');
    else toast.info('Du måste tillåta notiser i webbläsaren');
  };

  // Group events by occurrence_date
  const eventsByDate = events.reduce((acc, ev) => {
    (acc[ev.occurrence_date] = acc[ev.occurrence_date] || []).push(ev);
    return acc;
  }, {});

  const save = async (payload) => {
    try {
      if (editing && editing !== 'new') {
        await axios.patch(`${API}/calendar/events/${editing.event_id || editing.id}`, payload, { withCredentials: true });
        toast.success('Händelse uppdaterad');
      } else {
        await axios.post(`${API}/calendar/events`, payload, { withCredentials: true });
        toast.success('Händelse skapad ✨');
      }
      setEditing(null);
      setDefaultDate(null);
      await fetchEvents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Kunde inte spara'); }
  };

  const del = async (ev) => {
    if (!window.confirm(`Ta bort "${ev.title}"${ev.recurrence !== 'none' ? ' (alla förekomster)' : ''}?`)) return;
    try {
      await axios.delete(`${API}/calendar/events/${ev.event_id || ev.id}`, { withCredentials: true });
      setEditing(null);
      await fetchEvents();
      toast.success('Borttagen');
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const toggleTask = async (task) => {
    try {
      const endpoint = task.is_completed
        ? `${API}/tasks/${task.id}/uncomplete`
        : `${API}/tasks/${task.id}/complete`;
      await axios.post(endpoint, {}, { withCredentials: true });
      onPointsChange?.();
      await fetchEvents();
    } catch { toast.error('Kunde inte uppdatera task'); }
  };

  const toggleEventOccurrence = async (ev) => {
    try {
      const endpoint = ev.completed ? `${API}/calendar/events/uncomplete` : `${API}/calendar/events/complete`;
      await axios.post(endpoint, { event_id: ev.event_id || ev.id, occurrence_date: ev.occurrence_date }, { withCredentials: true });
      await fetchEvents();
    } catch { toast.error('Kunde inte uppdatera händelse'); }
  };

  const prevMonth = () => setCursor((c) => c.month === 0 ? { year: c.year - 1, month: 11 } : { year: c.year, month: c.month - 1 });
  const nextMonth = () => setCursor((c) => c.month === 11 ? { year: c.year + 1, month: 0 } : { year: c.year, month: c.month + 1 });
  const today = () => { const d = new Date(); setCursor({ year: d.getFullYear(), month: d.getMonth() }); };

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Laddar kalendern…</div>;

  const columns = buildColumnDays(cursor.year, cursor.month);
  const notifReady = typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted';

  return (
    <div className="space-y-4" data-testid="calendar-page">
      {/* Header with month nav */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-bold font-['Fredoka'] flex items-center gap-2">
          <CalendarDays className="w-6 h-6" /> Kalender
        </h2>
        <button
          onClick={requestNotif}
          className={`neo-btn px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${notifReady ? 'bg-[#C8B6FF]' : 'bg-white'}`}
          data-testid="calendar-notif-btn"
        >
          <Bell className="w-3 h-3" /> {notifReady ? 'Notiser på' : 'Påminn mig'}
        </button>
      </div>

      <div className="flex items-center justify-between neo-card p-2 bg-white">
        <button onClick={prevMonth} className="neo-btn p-2 bg-white" data-testid="calendar-prev"><ChevronLeft className="w-4 h-4" /></button>
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs opacity-60">Month</span>
          <h3 className="font-bold font-['Fredoka'] text-lg">{MONTHS_SV[cursor.month]} {cursor.year}</h3>
          <button onClick={today} className="neo-btn px-2 py-1 text-xs bg-[#FFD6E8]" data-testid="calendar-today">Idag</button>
          <button
            onClick={() => { setShiftDate(new Date()); setShiftOpen(true); }}
            className="neo-btn px-2 py-1 text-xs bg-[#B9F5D8] flex items-center gap-1"
            data-testid="calendar-add-shift"
            title="Lägg till arbetspass (söker bland mottagningar)"
          >
            <Briefcase className="w-3 h-3" /> Jobb
          </button>
        </div>
        <button onClick={nextMonth} className="neo-btn p-2 bg-white" data-testid="calendar-next"><ChevronRight className="w-4 h-4" /></button>
      </div>

      {/* Bullet-journal two-page spread: Mon-Thu | Fri-Sun + Tasks/Notes */}
      <div className="neo-card p-3 bg-[#FAF7FF] border-[3px]" data-testid="bujo-spread">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* LEFT PAGE: Monday – Thursday */}
          <div className="grid grid-cols-4 gap-0 border-[2.5px] border-black rounded-xl overflow-hidden bg-white">
            {[0, 1, 2, 3].map((wd) => (
              <div key={wd} className="border-r-[2.5px] border-black last:border-r-0" style={{ backgroundColor: COLUMN_PASTELS[wd] }}>
                <div className="text-center py-0.5 border-b-[2.5px] border-black bg-white/60">
                  <p className="font-mono text-[10px] opacity-70 leading-tight">{WEEKDAYS_SHORT[wd]}</p>
                  <p className="text-[8px] opacity-50 -mt-0.5 leading-tight">{WEEKDAYS_SV_FULL[wd]}</p>
                </div>
                {columns[wd].map((d, i) => (
                  <DayCell
                    key={i}
                    date={d}
                    events={eventsByDate}
                    categories={meta.categories}
                    currentMonth={cursor.month}
                    onClick={(d) => { setSelectedDay(d); }}
                    onEventClick={(ev) => setEditing(ev)}
                  />
                ))}
              </div>
            ))}
          </div>

          {/* RIGHT PAGE: Friday – Sunday + Tasks + Notes sidebar */}
          <div className="grid grid-cols-[1fr_1fr_1fr_140px] md:grid-cols-[1fr_1fr_1fr_170px] gap-0 border-[2.5px] border-black rounded-xl overflow-hidden bg-white">
            {[4, 5, 6].map((wd) => (
              <div key={wd} className="border-r-[2.5px] border-black" style={{ backgroundColor: COLUMN_PASTELS[wd] }}>
                <div className="text-center py-0.5 border-b-[2.5px] border-black bg-white/60">
                  <p className="font-mono text-[10px] opacity-70 leading-tight">{WEEKDAYS_SHORT[wd]}</p>
                  <p className="text-[8px] opacity-50 -mt-0.5 leading-tight">{WEEKDAYS_SV_FULL[wd]}</p>
                </div>
                {columns[wd].map((d, i) => (
                  <DayCell
                    key={i}
                    date={d}
                    events={eventsByDate}
                    categories={meta.categories}
                    currentMonth={cursor.month}
                    onClick={(d) => { setSelectedDay(d); }}
                    onEventClick={(ev) => setEditing(ev)}
                  />
                ))}
              </div>
            ))}
            {/* Side panel: Att göra (top) + Notes (bottom) */}
            <div className="flex flex-col gap-0">
              <div className="flex-1 p-1.5 border-b-[2.5px] border-black overflow-y-auto" style={{ backgroundColor: '#FFE8F0', maxHeight: '220px' }}>
                <h3 className="font-['Fredoka'] font-bold text-xs mb-1">Att göra</h3>
                <div className="space-y-0.5">
                  {(() => {
                    // Upcoming events (today + next 6 days)
                    const today = new Date(); today.setHours(0, 0, 0, 0);
                    const todayStr = fmtDateLocal(today);
                    const weekAhead = new Date(today); weekAhead.setDate(weekAhead.getDate() + 6);
                    const weekAheadStr = fmtDateLocal(weekAhead);
                    const upcomingEvents = events
                      .filter((ev) => ev.occurrence_date >= todayStr && ev.occurrence_date <= weekAheadStr)
                      .sort((a, b) => (a.occurrence_date + (a.starts_at || '')).localeCompare(b.occurrence_date + (b.starts_at || '')))
                      .slice(0, 10);
                    const items = [
                      ...upcomingEvents.map((ev) => ({ kind: 'event', ev })),
                      ...tasks.slice(0, 10).map((t) => ({ kind: 'task', t })),
                    ];
                    if (items.length === 0) {
                      return <p className="text-[10px] opacity-60 italic">Inga uppgifter</p>;
                    }
                    return items.map((it) => {
                      if (it.kind === 'event') {
                        const ev = it.ev;
                        const c = meta.categories[ev.category] || { emoji: '•' };
                        const isToday = ev.occurrence_date === todayStr;
                        return (
                          <label key={`ev-${ev.event_id || ev.id}-${ev.occurrence_date}`} className="flex items-start gap-1 text-[10px] cursor-pointer" data-testid={`bujo-event-${ev.event_id || ev.id}-${ev.occurrence_date}`}>
                            <input
                              type="checkbox"
                              checked={!!ev.completed}
                              onChange={() => toggleEventOccurrence(ev)}
                              className="mt-0.5 flex-shrink-0"
                            />
                            <span className={`${ev.completed ? 'line-through opacity-50' : ''} break-words leading-tight flex-1 min-w-0`}>
                              <span className="mr-0.5">{c.emoji}</span>
                              <span className="font-bold">{ev.title}</span>
                              <span className="opacity-60 ml-1 font-mono text-[9px]">
                                {isToday ? 'idag' : ev.occurrence_date.slice(5)}
                                {!ev.all_day && ev.starts_at ? ` · ${fmtTime(ev.starts_at)}` : ''}
                              </span>
                            </span>
                          </label>
                        );
                      }
                      const t = it.t;
                      return (
                        <label key={`t-${t.id}`} className="flex items-start gap-1 text-[10px] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!t.is_completed}
                            onChange={() => toggleTask(t)}
                            className="mt-0.5 flex-shrink-0"
                            data-testid={`bujo-task-${t.id}`}
                          />
                          <span className={`${t.is_completed ? 'line-through opacity-50' : ''} break-words leading-tight`}>{t.title}</span>
                        </label>
                      );
                    });
                  })()}
                </div>
              </div>
              <div className="flex-1 p-1.5" style={{ backgroundColor: '#E8F0FF' }}>
                <h3 className="font-['Fredoka'] font-bold text-xs mb-1">Notes</h3>
                <textarea
                  value={notes}
                  onChange={(e) => saveNotes(e.target.value)}
                  placeholder="Skriv…"
                  className="w-full h-full min-h-[110px] bg-transparent outline-none text-[10px] resize-none leading-relaxed"
                  data-testid="bujo-notes-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Helper text */}
        <p className="text-[10px] opacity-60 mt-2 text-center italic">
          💡 Klicka på en dag för att se planering · klicka på en befintlig för att redigera
        </p>
      </div>

      {/* Day detail: Daily Planner + events for selected day */}
      {selectedDay && (
        <DayDetail
          date={selectedDay}
          events={eventsByDate[fmtDateLocal(selectedDay)] || []}
          categories={meta.categories}
          onClose={() => setSelectedDay(null)}
          onAddEvent={() => { setDefaultDate(selectedDay); setEditing('new'); }}
          onAddShift={() => { setShiftDate(selectedDay); setShiftOpen(true); }}
          onEditEvent={(ev) => setEditing(ev)}
          onDeleteEvent={del}
        />
      )}

      {/* Editor modal */}
      {editing && (
        <EventEditor
          existing={editing === 'new' ? null : editing}
          categories={meta.categories}
          recurrenceOptions={meta.recurrence_options}
          reminderOptions={meta.reminder_options}
          defaultDate={defaultDate}
          onSave={save}
          onCancel={() => { setEditing(null); setDefaultDate(null); }}
          onDelete={del}
        />
      )}

      {/* Collapsible: older overview + sync */}
      <details className="neo-card bg-white p-3" data-testid="calendar-overview-details">
        <summary className="cursor-pointer font-bold font-['Fredoka'] text-sm flex items-center gap-2">
          <ChevronDown className="w-4 h-4" />
          Översikt: Schema · Fokus · Vanor
        </summary>
        <div className="mt-3"><CalendarOverview /></div>
      </details>

      <details className="neo-card bg-white p-3" data-testid="calendar-sync-details">
        <summary className="cursor-pointer font-bold font-['Fredoka'] text-sm flex items-center gap-2">
          <ChevronDown className="w-4 h-4" />
          Synka med andra kalendrar (Google / Apple / Outlook)
        </summary>
        <div className="mt-3"><CalendarSync /></div>
      </details>

      {shiftOpen && (
        <WorkShiftQuickAdd
          initialDate={shiftDate ? fmtDateLocal(shiftDate) : fmtDateLocal(new Date())}
          onClose={() => setShiftOpen(false)}
          onShiftCreated={() => fetchEvents()}
        />
      )}
    </div>
  );
}
